import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Biotech,
  Inventory2,
  LocalDining,
  Science,
  TableRows,
} from '@mui/icons-material';

const tabs = [
  { label: 'Contents', icon: <TableRows sx={{ fontSize: 14 }} /> },
  { label: 'Nutrition', icon: <LocalDining sx={{ fontSize: 14 }} />, active: true },
  { label: 'Aminogram', icon: <Biotech sx={{ fontSize: 14 }} /> },
  { label: 'Physico Chemical', icon: <Science sx={{ fontSize: 14 }} /> },
];

const nutritionRows = [
  { label: 'Energy', value: '380 kcal / 1590 kJ' },
  { label: 'Protein', value: '80 g' },
  { label: 'Carbohydrate', value: '8.0 g' },
  { label: 'Sugars', value: '6.0 g' },
  { label: 'Fat', value: '5.5 g' },
  { label: 'Salt', value: '1.2 g' },
];

const physicoChemicalRows = [
  { label: 'Physical state', value: 'Solid' },
  { label: 'Moisture', value: '< 6%' },
  { label: 'pH', value: '6.2' },
  { label: 'Solubility', value: 'High' },
];

const aminogramRows = [
  { label: 'Leucine', value: '9.1 g' },
  { label: 'Lysine', value: '7.8 g' },
  { label: 'Valine', value: '5.4 g' },
];

function MiniValue({ label, value }) {
  return (
    <Box
      sx={{
        px: 0.85,
        py: 0.58,
        borderRadius: 1.4,
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.045),
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          fontWeight: 800,
          fontSize: 9.5,
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.25,
          fontWeight: 900,
          fontSize: 10.3,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionHeader({ icon, title, right }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.05,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.035),
      }}
    >
      <Stack direction="row" spacing={0.65} alignItems="center">
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 900,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.35,
          }}
        >
          {title}
        </Typography>
      </Stack>

      {right}
    </Stack>
  );
}

export default function IngredientNutritionTechnicalVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 310,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.15,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalDining sx={{ fontSize: 16 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Nutrition and technical data
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Review nutrition, aminogram and physicochemical properties from the ingredient record
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Structured data"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Stack>

        {/* Tabs */}
        <Box
          sx={{
            px: 1.4,
            py: 0.65,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
            overflow: 'hidden',
          }}
        >
          <Stack direction="row" spacing={0.45} sx={{ minWidth: 0 }}>
            {tabs.map((tab) => (
              <Box
                key={tab.label}
                sx={{
                  px: 0.75,
                  py: 0.45,
                  borderRadius: 1.4,
                  color: tab.active ? 'primary.main' : 'text.secondary',
                  bgcolor: tab.active
                    ? (theme) => alpha(theme.palette.primary.main, 0.08)
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontSize: 9.6,
                  fontWeight: 900,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.icon}
                {tab.label}
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr' },
            gap: 1,
            p: 1.5,
            minHeight: 235,
            overflow: 'hidden',
          }}
        >
          {/* Left */}
          <Box sx={{ minWidth: 0 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.15,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                mb: 1,
              }}
            >
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Inventory2 sx={{ fontSize: 16 }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 900,
                      fontSize: 11.2,
                      lineHeight: 1.15,
                    }}
                  >
                    Whey protein concentrate
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 9.8,
                      mt: 0.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    High-protein dairy powder for nutritional formulations
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <SectionHeader
                icon={<LocalDining sx={{ fontSize: 15 }} />}
                title="Nutrition per 100 g"
                right={
                  <Box
                    sx={{
                      px: 0.65,
                      py: 0.28,
                      borderRadius: 999,
                      fontSize: 9.4,
                      fontWeight: 900,
                      color: 'success.dark',
                      bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                    }}
                  >
                    Complete
                  </Box>
                }
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.85fr)',
                  columnGap: 1,
                  px: 1.05,
                  py: 0.65,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {['Parameter', 'Value'].map((head) => (
                  <Typography
                    key={head}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 9.7 }}
                  >
                    {head}
                  </Typography>
                ))}
              </Box>

              {nutritionRows.map((row, index) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.85fr)',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.05,
                    py: 0.67,
                    borderBottom: index !== nutritionRows.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      fontSize: 10.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.label}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 800,
                      fontSize: 10.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Box>

          {/* Right */}
          <Box
            sx={{
              minWidth: 0,
              display: 'grid',
              gridTemplateRows: 'auto auto 1fr',
              gap: 1,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <SectionHeader
                icon={<Science sx={{ fontSize: 15 }} />}
                title="Physico chemical"
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 0.65,
                  p: 1,
                }}
              >
                {physicoChemicalRows.map((item) => (
                  <MiniValue key={item.label} label={item.label} value={item.value} />
                ))}
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <SectionHeader
                icon={<Biotech sx={{ fontSize: 15 }} />}
                title="Aminogram"
                right={
                  <Box
                    sx={{
                      px: 0.65,
                      py: 0.28,
                      borderRadius: 999,
                      fontSize: 9.4,
                      fontWeight: 900,
                      color: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    Profile
                  </Box>
                }
              />

              <Stack spacing={0.55} sx={{ p: 1 }}>
                {aminogramRows.map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 0.8,
                      py: 0.45,
                      borderRadius: 1.3,
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.045),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 900, fontSize: 10 }}
                    >
                      {item.label}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 800, fontSize: 10 }}
                    >
                      {item.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}